import { addMilliseconds, endOfDay, startOfDay } from 'date-fns';
import * as React from 'react';
import { createContext } from 'react';
import useBookingsQuery from 'src/hooks/api/useBookingsQuery';
import useOfficesQuery from 'src/hooks/api/useOfficesQuery';
import useRoomsQuery from 'src/hooks/api/useRoomsQuery';
import useCalendar from 'src/hooks/useCalendar';
import { parseApiDate, today } from '../../../lib/date';
import useUsersQuery from 'src/hooks/api/useUsersQuery';
import useCreateBookingMutation from 'src/hooks/api/useCreateBookingMutation';
import useDeleteBookingMutation from 'src/hooks/api/useDeleteBookingMutation';
import useMe from 'src/components/AuthContext/useMe';

interface ICalendarPageContext {
  bookings: Record<string, Odoo.Booking[]>;
  createBooking: (from: Date, to: Date, roomId: number, organizerId?: number) => Promise<void>;
  deleteBooking: (bookingId: number) => Promise<void>;
  offices: Odoo.Office[];
  rooms: Odoo.Room[];
  selectedDate: Date;
  setShowWeekends: React.Dispatch<React.SetStateAction<boolean>>;
  showWeekends: boolean;
  users: Odoo.User[];
  weeks: Date[][];
}

const CalendarPageContext = createContext<ICalendarPageContext>({
  bookings: {},
  createBooking: async () => {},
  deleteBooking: async () => {},
  offices: [],
  rooms: [],
  selectedDate: today(),
  setShowWeekends: () => {},
  showWeekends: false,
  users: [],
  weeks: [],
});

export const CalendarPageProvider: React.FC<{
  children: React.ReactNode;
  selectedDate: Date;
}> = ({ children, selectedDate }) => {
  const me = useMe();
  const { weeks, firstDay, lastDay } = useCalendar(selectedDate, { startOfWeek: 1 });
  const { data: officesData } = useOfficesQuery();
  const { data: roomsData } = useRoomsQuery();
  const { data: usersData } = useUsersQuery();

  const { mutate: createBookingMutation } = useCreateBookingMutation();
  const { mutate: deleteBookingMutation } = useDeleteBookingMutation();

  const [showWeekends, setShowWeekends] = React.useState(false);

  const [from, to] = React.useMemo(() => {
    const from = startOfDay(firstDay);
    const to = addMilliseconds(endOfDay(lastDay), 1);
    return [from, to];
  }, [firstDay, lastDay]);
  const { data: bookingsData } = useBookingsQuery({ from, to });

  const bookings = React.useMemo(() => {
    const map: Record<string, Odoo.Booking[]> = {};
    if (bookingsData == null) return map;

    weeks.forEach(week => {
      week.forEach(date => {
        const dateKey = date.toISOString();
        map[dateKey] = bookingsData.filter(booking => {
          const bookingStart = parseApiDate(booking.start_datetime);
          const bookingEnd = addMilliseconds(parseApiDate(booking.stop_datetime), -1);
          return (
            (bookingStart <= date && bookingEnd >= date) || (bookingStart >= date && bookingStart < endOfDay(date))
          );
        });
      });
    });

    return map;
  }, [bookingsData, weeks]);

  const createBooking = React.useCallback(
    async (from: Date, to: Date, roomId: number, organizerId?: number) => {
      const id = organizerId ?? me?.id;
      if (id == null) {
        throw new Error('Missing organizerId for booking creation');
      }
      return new Promise<void>(resolve =>
        createBookingMutation(
          { from, to, roomId, organizerId: id, bookingLabel: me?.display_name },
          { onSettled: () => resolve() }
        )
      );
    },
    [me?.id, me?.display_name]
  );

  const deleteBooking = React.useCallback(async (bookingId: number) => {
    return new Promise<void>(resolve => deleteBookingMutation({ id: bookingId }, { onSettled: () => resolve() }));
  }, []);

  return (
    <CalendarPageContext.Provider
      value={{
        bookings,
        createBooking,
        deleteBooking,
        offices: officesData ?? [],
        rooms: roomsData ?? [],
        selectedDate,
        showWeekends,
        setShowWeekends,
        users: usersData ?? [],
        weeks,
      }}
    >
      {children}
    </CalendarPageContext.Provider>
  );
};

export const useCalendarPageContext = () => React.useContext(CalendarPageContext);
